// Global Instructions Rule Applied!
//
// useInvestmentDetail — local-state hook for the standalone, multi-fund
// Investments screen. Parallel to useFundInvestmentDetail (the per-fund
// Investments tab) but the fund scope + as-of date are session options
// surfaced in the Filtering drawer's Options panel (no top-level selectors),
// matching the per-fund tab's UI shape. Empty fundIds = all funds. Persists
// aggregation / filters / sorting under PREF_SUBKEY_INVESTMENT_DETAIL on the
// `investments` menu_item prefs row; options are session-only.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  fetchInvestmentDetailFiltered,
  fetchInvestmentDetailFilterOptions,
  type FundInvestmentDetailPaginatedResponse
} from '../../funds/services/fundsService.ts'
import { resolveDimLabelColumn } from '../../funds/components/aggregations/aggregationDimensions.ts'
import { INVESTMENT_DETAIL_DEFAULT_AGGREGATION } from '../components/investmentAggregationDimensions.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import { mergeUserPreferenceRowData } from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  areSortConfigsEqual,
  areStringArraysEqual,
  getSubPreferenceRow,
  PREF_SUBKEY_INVESTMENT_DETAIL,
  PREF_TABLE_AGGREGATION,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableAggregationFromPreferenceRow,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow,
  type FilterValue,
  type SortConfig
} from '../../global_configs/tableViewPreferences.ts'
import { MENU_ITEM_KEY_INVESTMENTS } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { recordHasTotalLabel } from '../../../core/utils/recordRowBold.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'

export { INVESTMENT_DETAIL_DEFAULT_AGGREGATION }
const DEFAULT_FILTERS: FilterValue[] = []

/** Derive an auto-sort from an aggregation list (field_name wire form). */
function deriveSortFromAggregation(aggregation: string[]): SortConfig[] {
  const cfg = store.getState().globalConfigs ?? {}
  const reportingColumnDefaults: any[] = (cfg.reporting_column_defaults ?? []) as any[]
  const modules: any[] = (cfg.modules ?? []) as any[]
  const out: SortConfig[] = []
  for (const dim of aggregation) {
    const col = resolveDimLabelColumn(dim, reportingColumnDefaults, modules)
    if (!col) continue
    const fieldName = typeof col.field_name === 'string' ? col.field_name : ''
    if (!fieldName) continue
    if (!out.some((s) => s.key === fieldName)) {
      out.push({ key: fieldName, direction: 'asc' })
    }
  }
  return out
}

export interface InvestmentDetailOptions {
  /** Selected fund ids; empty = all funds. */
  fundIds: string[]
  /** As-of timestamp; null = server now(). */
  datetime: string | null
  includeWarehoused: boolean
  includeConvertedOut: boolean
  /** IRR is the dominant cost (cashflow assembly + per-row pyxirr); opt-in. */
  includeIrr: boolean
}

/** Defaults: all funds; as-of = null (server now() — the date picker shows a
 *  grey "Now" placeholder rather than a fixed stored date); no
 *  warehoused/converted-out; IRR off (the all-funds default is heavy — IRR
 *  is an explicit opt-in). */
function defaultOptions(): InvestmentDetailOptions {
  return {
    fundIds: [],
    datetime: null,
    includeWarehoused: false,
    includeConvertedOut: false,
    includeIrr: false
  }
}

function fundScopeKey(o: InvestmentDetailOptions): string {
  return [...o.fundIds].sort().join('|') + '@' + (o.datetime ?? '')
}

/**
 * @param presetFundIds When set, the hook runs in EMBEDDED mode (e.g. the
 * consolidated-fund Investments tab): the fund scope starts from the preset,
 * and user preferences are neither hydrated nor persisted so the embedded
 * grid can't cross-contaminate the standalone Investments screen.
 */
export function useInvestmentDetail(presetFundIds?: string[] | null) {
  const dispatch = useDispatch<AppDispatch>()
  const embeddedPreset = presetFundIds ?? null

  const { menuItemId, preferenceRow, persistPreference } = useMenuItemUserPreference(
    MENU_ITEM_KEY_INVESTMENTS
  )

  const [data, setData] = useState<Record<string, unknown>[]>([])
  const [filterOptions, setFilterOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  const [activeAggregation, setActiveAggregationLocal] = useState<string[]>(
    INVESTMENT_DETAIL_DEFAULT_AGGREGATION
  )
  const [activeFilters, setActiveFiltersLocal] = useState<FilterValue[]>(DEFAULT_FILTERS)
  const [activeSorting, setActiveSortingLocal] = useState<SortConfig[]>(() =>
    deriveSortFromAggregation(INVESTMENT_DETAIL_DEFAULT_AGGREGATION)
  )
  const [activeOptions, setActiveOptionsLocal] = useState<InvestmentDetailOptions>(() =>
    embeddedPreset ? { ...defaultOptions(), fundIds: embeddedPreset } : defaultOptions()
  )
  const [draftOptions, setDraftOptions] = useState<InvestmentDetailOptions>(() =>
    embeddedPreset ? { ...defaultOptions(), fundIds: embeddedPreset } : defaultOptions()
  )
  const [hasMore, setHasMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [prefsReady, setPrefsReady] = useState(false)
  const skipPersistRef = useRef(true)

  // 1. Hydrate aggregation/filters/sorting from prefs (under sub-key).
  //    Embedded mode skips hydration entirely - the embedded grid starts
  //    from defaults + the preset fund scope.
  useEffect(() => {
    if (embeddedPreset) {
      setPrefsReady(true)
      return
    }
    const sub = getSubPreferenceRow(preferenceRow, PREF_SUBKEY_INVESTMENT_DETAIL)
    const nextAggregation =
      resolveTableAggregationFromPreferenceRow(sub, INVESTMENT_DETAIL_DEFAULT_AGGREGATION) ??
      INVESTMENT_DETAIL_DEFAULT_AGGREGATION
    const nextFilters = resolveTableFiltersFromPreferenceRow(sub, DEFAULT_FILTERS)
    const nextSorting = resolveTableSortingFromPreferenceRow(
      sub,
      deriveSortFromAggregation(nextAggregation)
    )
    if (!areStringArraysEqual(nextAggregation, activeAggregation)) setActiveAggregationLocal(nextAggregation)
    if (!areFilterValuesEqual(nextFilters, activeFilters)) setActiveFiltersLocal(nextFilters)
    if (!areSortConfigsEqual(nextSorting, activeSorting)) setActiveSortingLocal(nextSorting)
    setPrefsReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferenceRow])

  // 2. Fetch filter options on fund-scope / as-of-date change.
  const scopeKey = fundScopeKey(activeOptions)
  useEffect(() => {
    let cancelled = false
    setFilterOptionsLoading(true)
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchInvestmentDetailFilterOptions(activeOptions.fundIds, token, {
        datetime: activeOptions.datetime
      })
      if (!cancelled) {
        setFilterOptions(res.data || {})
        setFilterOptionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey])

  // 2b. The Summary KPIs come straight off the grid's synthesized total
  //     row (view__investment_detail attaches summary__* keys computed from
  //     the SAME filtered set). Deriving here means the panel is always
  //     consistent with the grid and responds to every column filter /
  //     scope / date / option with no extra round-trip.
  const summary = useMemo<Record<string, unknown>>(() => {
    const total = data.find((r) => recordHasTotalLabel(r)) as Record<string, unknown> | undefined
    if (!total) return {}
    return {
      investments_fmt: total.summary__investments_fmt,
      companies_fmt: total.summary__companies_fmt,
      no_active_fmt: total.summary__no_active_fmt,
      no_exited_fmt: total.summary__no_exited_fmt
    }
  }, [data])

  // 3. Fetch data on scope/aggregation/filter/sort/option change.
  const fetchPage1 = useCallback(
    async (
      aggregation: string[],
      sorting: SortConfig[],
      filters: FilterValue[],
      opts: InvestmentDetailOptions
    ) => {
      setLoading(true)
      setError(null)
      try {
        const token = getAccessToken(store.getState().auth)
        const res: FundInvestmentDetailPaginatedResponse = await fetchInvestmentDetailFiltered(
          opts.fundIds,
          aggregation,
          sorting,
          filters,
          1,
          null,
          token,
          {
            datetime: opts.datetime,
            includeWarehoused: opts.includeWarehoused,
            includeConvertedOut: opts.includeConvertedOut,
            includeIrr: opts.includeIrr
          }
        )
        if (res.error) {
          setError(res.error)
          setData([])
          setHasMore(false)
        } else {
          setData(res.data)
          setHasMore(res.hasMore)
          setCurrentPage(1)
        }
      } finally {
        setLoading(false)
      }
    },
    []
  )

  useEffect(() => {
    if (!prefsReady) return
    void fetchPage1(activeAggregation, activeSorting, activeFilters, activeOptions)
  }, [prefsReady, activeAggregation, activeFilters, activeSorting, activeOptions, fetchPage1])

  // 4. Persist aggregation/filters/sorting to user prefs (under the sub-key).
  const persistMergedSubPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      // embedded mode (consolidated-fund tab) never writes preferences -
      // it must not cross-contaminate the standalone Investments screen
      if (embeddedPreset) return { success: true as const }
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const baseData = (row?.data ?? {}) as Record<string, unknown>
      const baseSubData = (baseData[PREF_SUBKEY_INVESTMENT_DETAIL] as Record<string, unknown>) || {}
      const newSubData: Record<string, unknown> = {
        ...baseSubData,
        ...extra,
        [PREF_TABLE_AGGREGATION]: activeAggregation,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const merged: Record<string, unknown> = {
        ...baseData,
        [PREF_SUBKEY_INVESTMENT_DETAIL]: newSubData
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeAggregation, activeFilters, activeSorting, persistPreference, dispatch, embeddedPreset]
  )

  useEffect(() => {
    if (embeddedPreset) return // embedded mode never persists preferences
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedSubPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist investment detail preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeAggregation, activeFilters, activeSorting, persistMergedSubPreference, embeddedPreset])

  // 5. Public handlers.
  const handleSetAggregation = useCallback(
    (aggregation: string[]) => {
      const cleaned = Array.isArray(aggregation) ? aggregation.filter((a) => typeof a === 'string' && a) : []
      const next = cleaned.length === 0 ? INVESTMENT_DETAIL_DEFAULT_AGGREGATION : cleaned
      if (areStringArraysEqual(next, activeAggregation)) return
      setActiveAggregationLocal(next)
      setActiveSortingLocal(deriveSortFromAggregation(next))
    },
    [activeAggregation]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      setActiveFiltersLocal(filters)
    },
    [activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      setActiveSortingLocal(sorting)
    },
    [activeSorting]
  )

  const handleClearFilters = useCallback(() => {
    setActiveFiltersLocal([])
  }, [])

  const handleClearSorting = useCallback(() => {
    setActiveSortingLocal(deriveSortFromAggregation(activeAggregation))
  }, [activeAggregation])

  const handleResetAll = useCallback(() => {
    const defaultSort = deriveSortFromAggregation(INVESTMENT_DETAIL_DEFAULT_AGGREGATION)
    if (embeddedPreset || !menuItemId) {
      setActiveAggregationLocal(INVESTMENT_DETAIL_DEFAULT_AGGREGATION)
      setActiveFiltersLocal(DEFAULT_FILTERS)
      setActiveSortingLocal(defaultSort)
      return
    }
    void (async () => {
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const baseData = (row?.data ?? {}) as Record<string, unknown>
      const merged = { ...baseData }
      delete (merged as any)[PREF_SUBKEY_INVESTMENT_DETAIL]
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        skipPersistRef.current = true
        setActiveAggregationLocal(INVESTMENT_DETAIL_DEFAULT_AGGREGATION)
        setActiveFiltersLocal(DEFAULT_FILTERS)
        setActiveSortingLocal(defaultSort)
      }
    })()
  }, [menuItemId, persistPreference, dispatch, embeddedPreset])

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => {
      return persistMergedSubPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames })
    },
    [persistMergedSubPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (embeddedPreset) return { success: true as const }
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const row = reports.find((p: any) => p.menu_item_id === menuItemId)
    const baseData = (row?.data ?? {}) as Record<string, unknown>
    const baseSubData = ((baseData[PREF_SUBKEY_INVESTMENT_DETAIL] as Record<string, unknown>) || {})
    const newSubData = { ...baseSubData }
    delete (newSubData as any)[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...baseData,
      [PREF_SUBKEY_INVESTMENT_DETAIL]: {
        ...newSubData,
        [PREF_TABLE_AGGREGATION]: activeAggregation,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      dispatch(mergeUserPreferenceRowData(menuItemId, merged))
    }
    return result
  }, [menuItemId, activeAggregation, activeFilters, activeSorting, persistPreference, dispatch, embeddedPreset])

  const refetch = useCallback(
    () => fetchPage1(activeAggregation, activeSorting, activeFilters, activeOptions),
    [fetchPage1, activeAggregation, activeSorting, activeFilters, activeOptions]
  )

  // Infinite-scroll page accumulation. Unlike the per-fund tab (small,
  // single page), the all-funds screen spans many pages. The RPC appends
  // the per-currency grand-total row(s) to EVERY page (the totals are over
  // the whole filtered set, identical on each page), so strip totals from
  // both the accumulated set and the incoming page, then re-pin one set of
  // totals at the end.
  const handleLoadMore = useCallback(
    async (page: number, pageSize: number) => {
      if (loadingMore || !hasMore) return
      setLoadingMore(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const res: FundInvestmentDetailPaginatedResponse = await fetchInvestmentDetailFiltered(
          activeOptions.fundIds,
          activeAggregation,
          activeSorting,
          activeFilters,
          page,
          pageSize,
          token,
          {
            datetime: activeOptions.datetime,
            includeWarehoused: activeOptions.includeWarehoused,
            includeConvertedOut: activeOptions.includeConvertedOut,
            includeIrr: activeOptions.includeIrr
          }
        )
        if (res.error) return
        const incomingRows = res.data.filter((r) => !recordHasTotalLabel(r))
        const totals = res.data.filter((r) => recordHasTotalLabel(r))
        setData((prev) => [...prev.filter((r) => !recordHasTotalLabel(r)), ...incomingRows, ...totals])
        setHasMore(res.hasMore)
        setCurrentPage(page)
      } finally {
        setLoadingMore(false)
      }
    },
    [loadingMore, hasMore, activeOptions, activeAggregation, activeSorting, activeFilters]
  )

  const handleSetDraftOptions = useCallback((opts: InvestmentDetailOptions) => {
    setDraftOptions(opts)
  }, [])

  const handleApplyOptions = useCallback(() => {
    setActiveOptionsLocal(draftOptions)
  }, [draftOptions])

  const handleCancelOptions = useCallback(() => {
    setDraftOptions(activeOptions)
  }, [activeOptions])

  const handleResetOptionsToDefault = useCallback(() => {
    const reset = defaultOptions()
    setDraftOptions(reset)
    setActiveOptionsLocal(reset)
  }, [])

  const subPreferenceRow = getSubPreferenceRow(preferenceRow, PREF_SUBKEY_INVESTMENT_DETAIL)

  const defaultTableOptions = useMemo(() => defaultOptions(), [])

  return {
    data,
    summary,
    loading,
    error,
    filterOptions,
    filterOptionsLoading,
    activeAggregation,
    activeFilters,
    activeSorting,
    activeOptions,
    draftOptions,
    hasMore,
    currentPage,
    loadingMore,
    handleLoadMore,
    preferenceRow: subPreferenceRow,
    handleSetAggregation,
    handleSetFilters,
    handleSetSorting,
    handleSetDraftOptions,
    handleApplyOptions,
    handleCancelOptions,
    handleResetOptionsToDefault,
    handleClearFilters,
    handleClearSorting,
    handleResetAll,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    refetch,
    defaultTableAggregation: INVESTMENT_DETAIL_DEFAULT_AGGREGATION,
    defaultTableFilters: DEFAULT_FILTERS,
    defaultTableSorting: deriveSortFromAggregation(INVESTMENT_DETAIL_DEFAULT_AGGREGATION),
    defaultTableOptions
  }
}
